import { useEffect, useState } from 'react';
import { Lock, Plus, Send } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { addVat, formatMoney } from '@nako/core';
import type { Invoice, InvoiceStatus } from '@nako/types';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { localYmd } from '@/lib/journal-postings';

interface CustomerLite {
  id: string;
  name: string;
  on_hold: boolean;
  hold_reason: string | null;
}

const STATUS_TONE: Record<InvoiceStatus, 'green' | 'red' | 'neutral' | 'accent' | 'blue'> = {
  draft: 'neutral',
  sent: 'blue',
  paid: 'green',
  partial: 'accent',
  overdue: 'red',
  cancelled: 'neutral',
};

export default function Invoices() {
  const business = useAuthStore((s) => s.business)!;
  const session = useAuthStore((s) => s.session);
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const toast = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id: '',
    customer_name: '',
    subtotal: 0,
    vat: false,
    due_date: '',
    notes: '',
  });

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    const [{ data: inv }, { data: cust }] = await Promise.all([
      supabase
        .from('invoices')
        .select('*')
        .eq('business_id', business.id)
        .order('issue_date', { ascending: false }),
      supabase
        .from('customers')
        .select('id, name, on_hold, hold_reason')
        .eq('business_id', business.id)
        .order('name'),
    ]);
    setInvoices(inv ?? []);
    setCustomers((cust ?? []) as CustomerLite[]);
  }

  const selectedCustomer = customers.find((c) => c.id === form.customer_id) ?? null;
  const isHeld = selectedCustomer?.on_hold ?? false;

  async function create() {
    if (!form.customer_name || !form.subtotal) {
      toast.show('Customer name and subtotal required', { tone: 'warning' });
      return;
    }
    if (isHeld) {
      toast.show(
        `${selectedCustomer?.name} is on credit hold (${selectedCustomer?.hold_reason ?? 'reason unspecified'}). Release the hold from Customers before invoicing.`,
        { tone: 'error', durationMs: 5000 },
      );
      return;
    }
    const v = form.vat
      ? addVat(form.subtotal)
      : { net: form.subtotal, vat: 0, gross: form.subtotal };

    // Claim the next invoice number atomically (no collisions across tabs/users).
    const { data: seq, error: seqErr } = await supabase.rpc('next_invoice_number', {
      p_business_id: business.id,
    });
    if (seqErr || typeof seq !== 'number') {
      toast.show(seqErr?.message ?? 'Could not allocate an invoice number — try again.', {
        tone: 'error',
      });
      return;
    }
    const yyyy = new Date().getFullYear();
    const number = `INV-${yyyy}-${String(seq).padStart(5, '0')}`;
    const { error } = await supabase.from('invoices').insert({
      business_id: business.id,
      invoice_number: number,
      customer_name: form.customer_name,
      customer_id: form.customer_id || null,
      issue_date: localYmd(),
      due_date: form.due_date || null,
      subtotal: v.net,
      vat_amount: v.vat,
      total: v.gross,
      status: 'draft',
      notes: form.notes || null,
    });
    if (error) {
      toast.show(error.message, { tone: 'error' });
      return;
    }
    toast.show('Invoice created.', { tone: 'success' });
    setOpen(false);
    // Form reset moved to dedicated block below for type-correctness
    setForm({
      customer_id: '',
      customer_name: '',
      subtotal: 0,
      vat: false,
      due_date: '',
      notes: '',
    });
    void load();
  }

  async function markPaid(inv: Invoice) {
    const round2 = (n: number) => Math.round(n * 100) / 100;
    await supabase
      .from('invoices')
      .update({ status: 'paid', paid_amount: inv.total })
      .eq('id', inv.id);

    // Post the cash receipt to the ledger so it reaches the dashboard + reports.
    // Idempotent: skip if a sale entry for this invoice already exists.
    const { data: existing } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('business_id', business.id)
      .eq('source', 'sale')
      .eq('source_id', inv.id)
      .maybeSingle();

    if (!existing) {
      const { data: accts } = await supabase
        .from('accounts')
        .select('id, gl_code')
        .eq('business_id', business.id);
      const acc = (code: string) =>
        (accts ?? []).find((a) => (a as { gl_code: string }).gl_code === code) as
          | { id: string }
          | undefined;
      const cash = acc('1000');
      const sales = acc('4000');
      const vatOut = acc('2100');
      const vat = round2(inv.vat_amount ?? 0);
      // If the VAT Output account is missing, credit the full total to Sales
      // instead of splitting — a Dr total / Cr (total - vat) entry would be
      // UNBALANCED (the VAT credit line below only posts when 2100 exists).
      const net = vatOut ? round2(inv.total - vat) : inv.total;

      if (cash && sales) {
        const { data: entry } = await supabase
          .from('journal_entries')
          .insert({
            business_id: business.id,
            entry_date: localYmd(),
            reference: inv.invoice_number,
            description: `Invoice paid: ${inv.customer_name}`,
            source: 'sale',
            source_id: inv.id,
            is_posted: true,
            posted_at: new Date().toISOString(),
            created_by: session?.user.id ?? null,
          })
          .select()
          .single();
        if (entry) {
          const eid = (entry as { id: string }).id;
          const lines: Array<{
            entry_id: string;
            account_id: string;
            debit: number;
            credit: number;
            vat_amount: number;
            vat_rate: number;
            description: string;
          }> = [
            {
              entry_id: eid,
              account_id: cash.id,
              debit: inv.total,
              credit: 0,
              vat_amount: 0,
              vat_rate: 0,
              description: `Cash received — ${inv.invoice_number}`,
            },
            {
              entry_id: eid,
              account_id: sales.id,
              debit: 0,
              credit: net,
              vat_amount: 0,
              vat_rate: 0,
              description: `Sales — ${inv.invoice_number}`,
            },
          ];
          if (vat > 0 && vatOut) {
            lines.push({
              entry_id: eid,
              account_id: vatOut.id,
              debit: 0,
              credit: vat,
              vat_amount: vat,
              vat_rate: 14,
              description: `VAT output — ${inv.invoice_number}`,
            });
          }
          const { error: linesErr } = await supabase.from('journal_lines').insert(lines);
          // Lines failed but the header committed — delete the orphan so it
          // can't corrupt the trial balance (Round-2 finding #1).
          if (linesErr) {
            await supabase.from('journal_entries').delete().eq('id', eid);
            toast.show(`Could not post the ledger entry: ${linesErr.message}`, { tone: 'error' });
          }
        }
      }
    }

    toast.show('Marked paid.', { tone: 'success' });
    void load();
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl">Invoices</h1>
        <Button leftIcon={<Plus size={16} />} onClick={() => setOpen(true)} size="sm">
          New
        </Button>
      </div>

      {invoices.length === 0 ? (
        <Card className="py-10 text-center">
          <Send size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted mb-4">No invoices yet — issue your first one.</p>
          <Button onClick={() => setOpen(true)} leftIcon={<Plus size={16} />}>
            Create first invoice
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <Card key={inv.id} compact className="flex items-center justify-between">
              <div>
                <p className="font-medium">{inv.customer_name}</p>
                <p className="text-muted font-mono text-xs">{inv.invoice_number}</p>
                <p className="text-muted text-xs">Due {inv.due_date ?? '—'}</p>
              </div>
              <div className="text-right">
                <p className="font-mono">{formatMoney(inv.total, regionCode)}</p>
                <Pill tone={STATUS_TONE[inv.status]} className="mt-1">
                  {inv.status}
                </Pill>
                {inv.status !== 'paid' && (
                  <button
                    onClick={() => markPaid(inv)}
                    className="text-accent ml-auto mt-1 block text-xs"
                  >
                    Mark paid
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New invoice">
        <div className="space-y-3">
          {customers.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Customer</label>
              <select
                value={form.customer_id}
                onChange={(e) => {
                  const id = e.target.value;
                  const c = customers.find((x) => x.id === id);
                  setForm({
                    ...form,
                    customer_id: id,
                    customer_name: c ? c.name : form.customer_name,
                  });
                }}
                className="min-h-tap rounded-btn border-line border bg-white px-3"
              >
                <option value="">— pick a customer or type a name below —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id} disabled={c.on_hold}>
                    {c.name}
                    {c.on_hold ? ' · ON HOLD' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isHeld && (
            <div className="bg-red/10 text-red rounded-card flex gap-2 p-3 text-xs">
              <Lock size={14} className="shrink-0" />
              <div>
                <p className="font-semibold">{selectedCustomer?.name} is on credit hold</p>
                <p>{selectedCustomer?.hold_reason ?? 'Reason not recorded'}.</p>
                <p>Release the hold from Customers before invoicing this account.</p>
              </div>
            </div>
          )}

          <Input
            label="Customer name (override)"
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          />
          <Input
            label={`Subtotal (${regionConfig.currencyCode}, before VAT)`}
            type="number"
            numeric
            value={form.subtotal || ''}
            onChange={(e) => setForm({ ...form, subtotal: Number(e.target.value) || 0 })}
            leftAddon={regionConfig.currencySymbol}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.vat}
              onChange={(e) => setForm({ ...form, vat: e.target.checked })}
            />
            Add {(regionConfig.vat.rate * 100).toFixed(0)}% VAT
          </label>
          <Input
            label="Due date"
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
          <Input
            label="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <Button full onClick={create} disabled={isHeld} leftIcon={<Send size={16} />}>
            Create invoice
          </Button>
        </div>
      </Modal>
    </main>
  );
}
